import { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useSiteImage } from '@/hooks/useSiteImage';
import { useSiteContentValue } from '@/hooks/useSiteContentValue';
import { usePageSections } from '@/hooks/usePageSections';
import { IMAGES } from '@/constants/images';
import '../../home/v2/home-v2.css';
import './brand-v2.css';

// New-design Brand Story (shown when useSiteDesign().isNew), a brand
// portfolio in four parts:
//   1. hero with the brand film playing behind it
//   2. the founder: a large boxed portrait and the founder's message
//   3. company profile: who The.K Seoul is, the facts, numbers and brands
//   4. the brand: what Rx.K Seoul means, why it exists, what it stands for
// Texts reuse the classic brandStory.* keys where they say the same thing,
// plus brandV2.* keys; photos, video and section switches are edited in
// Admin > Website > Brand Story > New design (page key 'brandStoryV2').

// The.K Seoul's brands; this site is Rx.K Seoul.
const BRANDS = [
  { key: 'rxk', name: 'Rx.K Seoul', logo: '/v2/brands/rxk.webp', href: '/', here: true },
  { key: 'pickk', name: 'Pick.K Seoul', logo: '/v2/brands/pickk.webp', href: 'https://pick.thekseoul.com', here: false },
  { key: 'medik', name: 'Medi.K Seoul', logo: '/v2/brands/medik.webp', href: 'https://medik.thekseoul.com', here: false },
];

const BROCHURE = 'https://api.thekseoul.com/storage/v1/object/public/rxk-content/legacy/40f179cb-177e-48d0-b0ea-2bb2be981e90_RxKSeoul_Premium_Brochure_EN.pdf';

function Svg({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {d.split('|').map((p) => (p.startsWith('c:') ? <circle key={p} cx={p.split(',')[1]} cy={p.split(',')[2]} r={p.split(',')[3]} /> : <path key={p} d={p} />))}
    </svg>
  );
}
const I = {
  flask: 'M9 3h6v5l4 10a2 2 0 0 1-2 3H7a2 2 0 0 1-2-3l4-10z|M8 14h8',
  shield: 'M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z|M9 12l2 2 4-4',
  steps: 'M4 18h4v-4h4v-4h4V6h4',
  tick: 'M5 12.5l4.5 4.5L19 7.5',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  lab: 'c:,12,9,5|M8.5 13.5L5 21h14l-3.5-7.5',
  factory: 'M3 21V10l6 4V10l6 4V6h6v15z',
  box: 'M3 7l9-4 9 4v10l-9 4-9-4z|M3 7l9 4 9-4|M12 11v10',
  qr: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z|M14 14h2v2h-2zM18 18h2v2h-2zM14 18h2|M18 14h2',
  door: 'M5 21V4h11v17|M16 6h3v15|M12 12v.5',
  play: 'M8 5l11 7-11 7z',
  globe: 'c:,12,12,9|M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18',
};

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const io = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    io.current = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.current?.unobserve(e.target);
          }
        }),
      { threshold: 0.15 },
    );
    const el = ref.current;
    return () => {
      io.current?.disconnect();
      el?.querySelectorAll('[data-io]').forEach((n) => n.removeAttribute('data-io'));
    };
  }, []);
  useEffect(() => {
    ref.current?.querySelectorAll('.rv:not(.in):not([data-io])').forEach((el) => {
      el.setAttribute('data-io', '');
      io.current?.observe(el);
    });
  });
  return ref;
}

// Counts a stat like "70%+" or "৳12,000+" up from zero once it is on screen.
function Count({ value }: { value: string }) {
  const ref = useRef<HTMLElement>(null);
  const m = value.match(/^(\D*)([\d,]+)(.*)$/);
  const target = m ? Number(m[2].replace(/,/g, '')) : 0;
  const [n, setN] = useState(m ? 0 : target);
  useEffect(() => {
    const el = ref.current;
    if (!el || !m || !target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setN(target);
      return;
    }
    let raf = 0;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const f = (t: number) => {
        const p = Math.min(1, (t - t0) / 1400);
        setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(f);
      };
      raf = requestAnimationFrame(f);
    });
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  // Years and other plain numbers keep their form; only numbers written with commas get them.
  const fmt = (x: number) => (m && m[2].includes(',') ? x.toLocaleString('en-US') : String(x));
  return <b ref={ref}>{m ? `${m[1]}${fmt(n)}${m[3]}` : value}</b>;
}

// A YouTube film as a silent background: always covers its box (like
// object-fit: cover), ignores the pointer, and fades in over the hero photo
// once the player has had a moment to start.
// A YouTube video as the hero background (when the Admin hero link is a
// YouTube link). Sized a little larger than cover to keep YouTube's edges out
// of view; mute / unmute goes through the IFrame API (enablejsapi=1).
function HeroYouTube({ id, muted, onReady }: { id: string; muted: boolean; onReady: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      // phones crop more, so the film's burned-in captions and YouTube's title stay out of view
      const w = Math.max(width, (height * 16) / 9) * (width < 700 ? 1.5 : 1.2);
      setSize({ w, h: (w * 9) / 16 });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const send = (func: string, args: unknown[] = []) => frame.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
    if (!on) return;
    if (muted) send('mute');
    else {
      send('unMute');
      send('setVolume', [100]);
      send('playVideo');
    }
  }, [muted, on]);
  const q = `autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&playsinline=1&modestbranding=1&rel=0&iv_load_policy=3&cc_load_policy=0&disablekb=1&fs=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
  return (
    <div className={`hbg hyt${on ? ' on' : ''}`} ref={box} aria-hidden="true">
      {size.w > 0 && (
        <iframe
          ref={frame}
          src={`https://www.youtube.com/embed/${id}?${q}`}
          title="Rx.K Seoul film"
          allow="autoplay; encrypted-media"
          tabIndex={-1}
          style={{ width: size.w, height: size.h }}
          onLoad={() =>
            window.setTimeout(() => {
              setOn(true);
              onReady();
            }, 1500)
          }
        />
      )}
    </div>
  );
}

export default function BrandStoryV2() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const show = usePageSections('brandStoryV2');
  const root = useReveal();
  const heroImg = useSiteImage('brandV2.hero', '/v2/brand-hero.webp');
  const problemImg = useSiteImage('brandStory.problem.image', 'https://static.readdy.ai/image/16758960e7c0c7cf7b247f911f106b4f/828b0dc0fb1c402bdaee12182d277157.jpeg');
  const productImg = useSiteImage('brandStory.company.productImage', '/v2/p/set3-hero.webp');
  const founderImg = useSiteImage('brandV2.founder', '');
  const badgeImg = useSiteImage('brandStory.company.logoBadge', '');
  const ctaImg = useSiteImage('brandV2.cta', '/v2/giftbox.webp');
  const launch = [
    useSiteImage('home.launchGallery.crowd', IMAGES.crowd),
    useSiteImage('home.launchGallery.consult', IMAGES.consult),
    useSiteImage('home.launchGallery.model', IMAGES.model),
    useSiteImage('home.launchGallery.booth', IMAGES.booth),
    useSiteImage('home.launchGallery.brandPanel', IMAGES.brandPanel),
  ];
  const concernImg = [
    useSiteImage('brandV2.concern1', '/v2/p/serum-c2.jpg'),
    useSiteImage('brandV2.concern2', '/v2/p/serum-c3.jpg'),
    useSiteImage('brandV2.concern3', '/v2/p/serum-c1.jpg'),
    useSiteImage('brandV2.concern4', '/v2/p/serum-c4.jpg'),
  ];

  // Brand film: uploaded video or YouTube link; until one is set the 3-step routine film is used.
  const filmSet = useSiteContentValue('brandV2.film.video', 'en', '');
  const filmUrl = filmSet || '/v2/vid-routine.mp4';
  const yt = filmUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/)?.[1];
  // The hero plays the video set in Admin (hero video, else the brand film:
  // an uploaded file, a video link or a YouTube link), or else the built-in
  // cut of the brand film
  // (/v2/brand-loop.mp4: clean shots with their own sound, no captions or
  // titles, crossfaded, 19 s). It starts muted; the Sound button unmutes it.
  // Admin's hero video, or else the brand film link, plays in the hero.
  const heroCustom = useSiteContentValue('brandV2.hero.video', 'en', '') || filmSet;
  const heroYt = heroCustom.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([\w-]{11})/)?.[1];
  const heroLoop = heroCustom || '/v2/brand-loop.mp4';
  const [heroOn, setHeroOn] = useState(false);
  // Browsers only autoplay muted video; the Sound button turns on the hero
  // video's own audio (the file's, or the YouTube video's).
  const heroVid = useRef<HTMLVideoElement>(null);
  const [heroMuted, setHeroMuted] = useState(true);
  const heroTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(heroTimer.current), []);
  const toggleSound = () => {
    const next = !heroMuted;
    const v = heroVid.current;
    if (v) {
      v.muted = next;
      if (!next) v.play().catch(() => {});
    }
    setHeroMuted(next);
  };
  const [filmOpen, setFilmOpen] = useState(false);
  useEffect(() => {
    if (!filmOpen) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && setFilmOpen(false);
    document.addEventListener('keydown', k);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', k);
      document.body.style.overflow = '';
    };
  }, [filmOpen]);

  // Scroll progress line, and the manifesto lighting up word by word.
  const mani = useRef<HTMLElement>(null);
  const progress = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      mani.current?.style.setProperty('--p', '1');
      return;
    }
    let raf = 0;
    const f = () => {
      raf = 0;
      const vh = window.innerHeight;
      const el = mani.current;
      if (el) {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--p', Math.max(0, Math.min(1, (vh * 0.9 - r.top) / (vh * 0.75))).toFixed(3));
      }
      const doc = document.documentElement;
      progress.current?.style.setProperty('--w', `${(window.scrollY / Math.max(1, doc.scrollHeight - vh)) * 100}%`);
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(f);
    };
    window.addEventListener('scroll', on, { passive: true });
    f();
    return () => {
      window.removeEventListener('scroll', on);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Founder frame follows the cursor a little; the grid drifts the other way.
  const drift = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
    el.style.setProperty('--my', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
  };
  const undrift = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.setProperty('--mx', '0');
    e.currentTarget.style.setProperty('--my', '0');
  };
  const quote = t('brandV2.manifesto.quote');
  const message = t('brandV2.founder.message')
    .split(/\n+/)
    .filter((x) => x.trim());
  const features = [
    { icon: I.flask, key: 'feature1' },
    { icon: I.shield, key: 'feature2' },
    { icon: I.steps, key: 'feature3' },
  ];
  const journey = [I.lab, I.factory, I.box, I.qr, I.door];
  const facts = [1, 2, 3, 4, 5, 6];

  return (
    <div className="v2 bsv" ref={root}>
      <div className="bprog" ref={progress} aria-hidden="true" />

      {/* 1. Hero: the brand film playing behind the heading */}
      {show('hero') && (
        <section className="bhero">
          <img className="hbg" src={heroImg} alt="" />
          {heroYt ? (
            <HeroYouTube id={heroYt} muted={heroMuted} onReady={() => setHeroOn(true)} />
          ) : (
            <video
              ref={heroVid}
              className={`hbg hvid${heroOn ? ' on' : ''}`}
              src={heroLoop}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
              onPlaying={(e) => {
                // The hero photo shows first; once the video is really playing it
                // fades in from its first frame after a short pause.
                if (heroOn || heroTimer.current) return;
                const v = e.currentTarget;
                heroTimer.current = window.setTimeout(() => {
                  v.currentTime = 0;
                  setHeroOn(true);
                }, 1500);
              }}
            />
          )}
          {heroOn && (
            <button
              type="button"
              className={`hsnd${heroMuted ? '' : ' on'}`}
              onClick={toggleSound}
              aria-pressed={!heroMuted}
              aria-label={heroMuted ? t('brandV2.hero.soundOn', 'Sound on') : t('brandV2.hero.soundOff', 'Sound off')}
              title={heroMuted ? t('brandV2.hero.soundOn', 'Sound on') : t('brandV2.hero.soundOff', 'Sound off')}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 9h4l5-4v14l-5-4H4z" />
                {heroMuted ? <path d="M17 9l5 6M22 9l-5 6" /> : <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />}
              </svg>
            </button>
          )}
          <div className="wrap">
            <span className="lbl rise">{t('brandStory.hero.label')}</span>
            <h1>
              <span className="ln">
                <span style={{ ['--d' as string]: '.15s' }}>{t('brandStory.hero.heading')}</span>
              </span>
              <span className="ln">
                <em style={{ ['--d' as string]: '.35s' }}>{t('brandStory.hero.subheading')}</em>
              </span>
            </h1>
            <p className="rise" style={{ ['--d' as string]: '.45s' }}>
              {t('brandStory.hero.subtitle')}
            </p>
            <div className="hacts rise" style={{ ['--d' as string]: '.55s' }}>
              <button type="button" className="btn wine" onClick={() => setFilmOpen(true)}>
                <Svg d={I.play} /> {t('brandV2.film.watch')}
              </button>
              <a className="btn glass" href="#bs-founder">
                {t('brandV2.hero.journey')} <Svg d={I.arrow} />
              </a>
            </div>
          </div>
          <a className="cue" href="#bs-founder" aria-label="Scroll down" />
        </section>
      )}

      {/* 2. The founder: large boxed portrait and the founder's message */}
      {show('founder') && (
        <section className="sec found" id="bs-founder">
          <div className="wrap">
            <div className="fport rv" onMouseMove={drift} onMouseLeave={undrift}>
              <span className="fgrid" aria-hidden="true" />
              <figure>
                <img src={founderImg || productImg} alt={t('brandStory.company.signatureName')} loading="lazy" />
                <span className="shine" aria-hidden="true" />
              </figure>
              {['tl', 'tr', 'bl', 'br'].map((c) => (
                <i key={c} className={`cb ${c}`} aria-hidden="true" />
              ))}
              <span className="dimv" aria-hidden="true">
                <small>THE.K SEOUL</small>
              </span>
              <span className="dimh">
                <b>{t('brandStory.company.signatureName')}</b>
                <small>{t('brandStory.company.signatureCaption')}</small>
              </span>
            </div>
            <div className="ftext rv" style={{ ['--d' as string]: '.15s' }}>
              <span className="kick">{t('brandV2.founder.label')}</span>
              <blockquote className="fq">{t('brandV2.founder.quote')}</blockquote>
              {message.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <div className="sigw">
                <em>{t('brandStory.company.signatureName')}</em>
                <small>{t('brandStory.company.signatureCaption')}</small>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 3. Company profile */}
      {show('company') && (
        <section className="sec cprof">
          <div className="wrap">
            <div className="cgrid">
              <div className="bhouse rv">
                <img className="parent" src="/v2/brands/thek.webp" alt="The.K Seoul" />
                <span className="tag">{t('brandV2.company.tag')}</span>
                <span className="tree" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <div className="kids">
                  {BRANDS.map((b) => (
                    <a key={b.key} href={b.href} className={b.here ? 'here' : ''}>
                      <img src={b.logo} alt={b.name} />
                    </a>
                  ))}
                </div>
              </div>
              <div className="cinfo rv" style={{ ['--d' as string]: '.1s' }}>
                <span className="kick">{t('brandV2.company.label')}</span>
                <h2>{t('brandV2.company.heading')}</h2>
                <p>{t('brandStory.company.p1')}</p>
                <div className="route" aria-hidden="true">
                  <span>
                    <b>SEOUL</b>
                    <small>{t('brandV2.company.routeFrom')}</small>
                  </span>
                  <i />
                  <span>
                    <b>DHAKA</b>
                    <small>{t('brandV2.company.routeTo')}</small>
                  </span>
                </div>
                <dl className="facts">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <div key={n}>
                      <dt>{t(`brandV2.company.f${n}l`)}</dt>
                      <dd>{t(`brandV2.company.f${n}v`)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="nband rv">
              {[1, 2, 3, 4].map((n) => (
                <div key={n}>
                  <Count value={t(`brandV2.glance.n${n}`)} />
                  <span>{t(`brandV2.glance.l${n}`)}</span>
                </div>
              ))}
            </div>

            <div className="fam">
              <div className="famhd rv">
                <span className="kick">{t('brandV2.company.familyLabel')}</span>
                <h3>{t('brandV2.company.familyHeading')}</h3>
              </div>
              <div className="famrow">
                {BRANDS.map((b, i) => (
                  <a key={b.key} className={`brand rv${b.here ? ' here' : ''}`} href={b.href} style={{ ['--d' as string]: `${i * 0.1}s` }}>
                    <span className="lg">
                      <img src={b.logo} alt={b.name} />
                    </span>
                    <small>{t(`brandV2.brands.${b.key}`)}</small>
                    <span className="go">
                      {b.here ? t('brandV2.brands.here') : b.href.replace('https://', '')}
                      {!b.here && <Svg d={I.arrow} />}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {show('launch') && (
        <section className="sec blaunch">
          <div className="wrap">
            <div className="hd center rv">
              <span className="kick">{t('homeV2.launch.label')}</span>
              <h2>
                {t('homeV2.launch.heading')} <em>{t('homeV2.launch.headingEm')}</em>
              </h2>
              <p>{t('homeV2.launch.subtitle')}</p>
            </div>
            <div className="lgrid">
              {launch.map((src, i) => (
                <figure key={i} className={`rv g${i + 1}`} style={{ ['--d' as string]: `${i * 0.07}s` }}>
                  <img src={src} alt={t(`homeV2.launch.cap${i + 1}`)} loading="lazy" />
                  <figcaption>{t(`homeV2.launch.cap${i + 1}`)}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 4. The brand: what the name means */}
      {show('name') && (
        <section className="sec bname">
          <div className="wrap">
            <div className="hd center rv">
              <span className="kick">{t('brandV2.name.label')}</span>
              <h2>{t('brandV2.name.heading')}</h2>
              <p>{t('brandV2.name.lead')}</p>
            </div>
            <div className="eq rv">
              {(['rx', 'k', 'seoul'] as const).map((k, i) => (
                <Fragment key={k}>
                  {i > 0 && (
                    <span className="op" aria-hidden="true">
                      +
                    </span>
                  )}
                  <article className="part" style={{ ['--i' as string]: i }}>
                    <span className="big">
                      <img src={`/v2/brands/part-${k}.webp`} alt={t(`brandV2.name.${k}.mark`)} />
                    </span>
                    <b>{t(`brandV2.name.${k}.title`)}</b>
                    <p>{t(`brandV2.name.${k}.text`)}</p>
                  </article>
                </Fragment>
              ))}
              <span className="op eqs" aria-hidden="true">
                =
              </span>
              <div className="result">
                <img src="/v2/brands/rxk.webp" alt="Rx.K Seoul" />
                <small>{t('brandV2.name.result')}</small>
              </div>
            </div>
          </div>
        </section>
      )}

      {show('problem') && (
        <section className="sec soft prob">
          <div className="wrap">
            <figure className="rv">
              <img src={problemImg} alt="" loading="lazy" />
            </figure>
            <div className="rv" style={{ ['--d' as string]: '.1s' }}>
              <span className="kick">{t('brandStory.problem.label')}</span>
              <h2>{t('brandStory.problem.heading')}</h2>
              <p>{t('brandStory.problem.p1')}</p>
              <p>{t('brandStory.problem.p2')}</p>
              <p className="em">{t('brandStory.problem.p3')}</p>
              <div className="stats">
                {[1, 2, 3].map((n) => (
                  <div key={n}>
                    <Count value={t(`brandStory.problem.stat${n}`)} />
                    <span>{t(`brandStory.problem.stat${n}Label`)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {show('manifesto') && (
        <section className="sec mani" ref={mani}>
          <div className="wrap">
            <p className="q" style={{ ['--n' as string]: quote.split(' ').length }}>
              {quote.split(' ').map((w, i) => (
                <Fragment key={i}>
                  <span style={{ ['--i' as string]: i }}>{w}</span>{' '}
                </Fragment>
              ))}
            </p>
            <p className="tx rv">{t('brandV2.manifesto.text')}</p>
          </div>
        </section>
      )}

      {show('solution') && (
        <section className="sec soft sol">
          <div className="wrap">
            <div className="hd rv">
              <span className="kick">{t('brandStory.solution.label')}</span>
              <h2>{t('brandStory.solution.heading')}</h2>
              <p>{t('brandStory.solution.body')}</p>
            </div>
            <div className="cards">
              {features.map((f, i) => (
                <article key={f.key} className="rv" style={{ ['--d' as string]: `${i * 0.1}s` }}>
                  <span className="no">0{i + 1}</span>
                  <span className="ic">
                    <Svg d={f.icon} />
                  </span>
                  <h3>{t(`brandStory.solution.${f.key}.title`)}</h3>
                  <ul>
                    {[1, 2, 3, 4].map((n) => (
                      <li key={n}>
                        <Svg d={I.tick} />
                        {t(`brandStory.solution.${f.key}.point${n}`)}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {show('journey') && (
        <section className="sec jour">
          <div className="wrap">
            <div className="hd center rv">
              <span className="kick">{t('brandV2.journey.label')}</span>
              <h2>
                {t('brandV2.journey.heading')} <em>{t('brandV2.journey.headingEm')}</em>
              </h2>
            </div>
            <ol className="path rv">
              {journey.map((icon, i) => (
                <li key={i} style={{ ['--i' as string]: i }}>
                  <span className="ic">
                    <Svg d={icon} />
                  </span>
                  <b>{t(`brandV2.journey.step${i + 1}.title`)}</b>
                  <p>{t(`brandV2.journey.step${i + 1}.text`)}</p>
                </li>
              ))}
            </ol>
            <div className="jcta rv">
              <Link className="btn ghost" to={prefix + '/verify'}>
                {t('brandV2.journey.verify')} <Svg d={I.arrow} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {show('madeFor') && (
        <section className="sec soft made">
          <div className="wrap">
            <div className="top">
              <div className="rv">
                <span className="kick">{t('brandStory.madeFor.label')}</span>
                <h2>{t('brandStory.madeFor.heading')}</h2>
              </div>
              <p className="rv" style={{ ['--d' as string]: '.1s' }}>
                {t('brandStory.madeFor.p1')}
              </p>
            </div>
            <div className="tiles">
              {[1, 2, 3, 4].map((n) => (
                <article key={n} className="rv" style={{ ['--d' as string]: `${(n - 1) * 0.08}s` }}>
                  <img src={concernImg[n - 1]} alt="" loading="lazy" />
                  <div>
                    <small>0{n}</small>
                    <b>{t(`brandStory.madeFor.concern${n}.title`)}</b>
                    <p>{t(`brandStory.madeFor.concern${n}.desc`)}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="bottom rv">
              <p>{t('brandStory.madeFor.p2')}</p>
              <Link className="btn wine" to={prefix + '/3-step-treatment'}>
                {t('brandStory.madeFor.cta')}
              </Link>
            </div>
          </div>
        </section>
      )}

      {show('cta') && (
        <section className="sec bcta">
          <div className="wrap">
            <div className="band rv">
              <img src={ctaImg} alt="" loading="lazy" />
              <div>
                <h2>{t('brandStory.cta.heading')}</h2>
                <p>{t('brandStory.cta.body')}</p>
                <div className="bacts">
                  <Link className="btn wine" to={prefix + '/3-step-treatment'}>
                    {t('brandV2.cta.shop')}
                  </Link>
                  <Link className="btn ghost" to={prefix + '/stores'}>
                    {t('common.whereToBuy', 'Where to buy')}
                  </Link>
                  <a className="dl" href={BROCHURE} target="_blank" rel="noopener noreferrer">
                    {t('brandStory.cta.downloadBrochure')} <Svg d={I.arrow} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {filmOpen && (
        <div className="fmodal" role="dialog" aria-modal="true" aria-label={t('brandV2.film.watch')} onClick={() => setFilmOpen(false)}>
          <div className="fbox2" onClick={(e) => e.stopPropagation()}>
            {yt ? (
              <iframe src={`https://www.youtube.com/embed/${yt}?autoplay=1&rel=0`} title="Rx.K Seoul film" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
            ) : (
              <video src={filmUrl} controls autoPlay playsInline />
            )}
          </div>
          <button type="button" className="fx" aria-label="Close" onClick={() => setFilmOpen(false)}>
            ×
          </button>
        </div>
      )}
    </div>
  );
}
